import { useCallback, useEffect, useMemo, useRef } from 'react'

/**
 * Detects the platform-appropriate modifier key
 * @returns {string} 'metaKey' for Mac, 'ctrlKey' for others
 */
const getModifierKeyProperty = () => {
    if (typeof window === 'undefined') return 'ctrlKey'

    const userAgent = window.navigator?.userAgent?.toLowerCase() || ''
    return userAgent.includes('mac') ? 'metaKey' : 'ctrlKey'
}

/**
 * Normalizes key names to handle common variations
 * @param {string} key - Key name to normalize
 * @returns {string} Normalized key name
 */
const normalizeKey = (key) => {
    const keyMap = {
        cmd: 'Meta',
        command: 'Meta',
        ctrl: 'Control',
        esc: 'Escape',
        del: 'Delete',
        space: ' ',
        enter: 'Enter',
        return: 'Enter',
    }

    return keyMap[key.toLowerCase()] || key
}

/**
 * Checks if the target element is an input where shortcuts should be ignored
 * @param {Element} target - Event target element
 * @returns {boolean} True if target is an input element
 */
const isInputElement = (target) => {
    if (!target) return false

    const tagName = target.tagName || target.nodeName || ''
    const contentEditable = target.contentEditable || 'false'

    return tagName === 'INPUT' || tagName === 'TEXTAREA' || contentEditable === 'true' || contentEditable === true
}

/**
 * Simple, performant keyboard shortcut hook
 *
 * @param {string|string[]} keys - Key or array of keys for the shortcut
 * @param {Function} callback - Function to call when shortcut is triggered
 * @param {Object} options - Configuration options
 * @param {boolean} [options.disabled=false] - Whether to disable the shortcut
 * @param {boolean} [options.preventDefault=true] - Whether to prevent default behavior
 * @param {boolean} [options.stopPropagation=true] - Whether to stop event propagation
 * @param {boolean} [options.enableInInputs=false] - Whether to trigger in input elements
 * @param {'document'|'window'} [options.target='document'] - Event target (document or window)
 *
 * @example
 * // Simple single key
 * useSimpleKeyboardShortcut('Escape', handleEscape)
 *
 * @example
 * // Key combination with Alt
 * useSimpleKeyboardShortcut(['Alt', 'c'], handleAltC)
 *
 * @example
 * // Cross-platform modifier key
 * useSimpleKeyboardShortcut(['Modifier', 'a'], handleSelectAll)
 *
 * @example
 * // With options
 * useSimpleKeyboardShortcut('Delete', handleDelete, {
 *   disabled: viewOnlyMode,
 *   preventDefault: true
 * })
 */
const useSimpleKeyboardShortcut = (keys, callback, options = {}) => {
    // Validate parameters
    if (!keys) {
        throw new Error('useSimpleKeyboardShortcut: keys parameter is required')
    }

    if (typeof callback !== 'function') {
        throw new Error('useSimpleKeyboardShortcut: callback must be a function')
    }

    // Extract options with defaults
    const {
        disabled = false,
        enableInInputs = false,
        preventDefault = true,
        stopPropagation = true,
        target = 'document',
    } = options

    // Normalize keys to array and handle platform differences
    const normalizedKeys = useMemo(() => {
        const keyArray = Array.isArray(keys) ? keys : [keys]
        const modifierKeyProperty = getModifierKeyProperty()

        return keyArray.map((key) => {
            const normalized = normalizeKey(key)
            // Replace 'Modifier' with platform-appropriate key
            return normalized === 'Modifier' ? (modifierKeyProperty === 'metaKey' ? 'Meta' : 'Control') : normalized
        })
    }, [keys])

    // Store callback in ref to avoid stale closures
    const callbackRef = useRef(callback)
    useEffect(() => {
        callbackRef.current = callback
    }, [callback])

    // Memoized event handler for performance
    const handleKeyDown = useCallback(
        /** @param {KeyboardEvent} event */ (event) => {
            // Early exit if disabled
            if (disabled) return

            // Check if we should ignore based on target
            if (!enableInInputs && isInputElement(/** @type {Element} */ (event.target))) {
                return
            }

            // Check if all required keys match
            const isMatch = normalizedKeys.every((key) => {
                switch (key) {
                    case 'Control':
                        return event.ctrlKey
                    case 'Meta':
                        return event.metaKey
                    case 'Alt':
                        return event.altKey
                    case 'Shift':
                        return event.shiftKey
                    default:
                        return event.key === key || event.code === `Key${key.toUpperCase()}`
                }
            })

            if (!isMatch) return

            // Handle event prevention
            if (preventDefault && typeof event.preventDefault === 'function') {
                event.preventDefault()
            }

            if (stopPropagation && typeof event.stopPropagation === 'function') {
                event.stopPropagation()
            }

            // Call the callback
            callbackRef.current(event)
        },
        [normalizedKeys, disabled, preventDefault, stopPropagation, enableInInputs]
    )

    // Set up and clean up event listeners
    useEffect(() => {
        if (disabled) return

        // Select target element
        const targetElement = target === 'window' ? window : document

        // Check if target is available
        if (!targetElement || typeof targetElement.addEventListener !== 'function') {
            return
        }

        // Add event listener
        targetElement.addEventListener('keydown', handleKeyDown, false)

        // Cleanup function
        return () => {
            if (typeof targetElement.removeEventListener === 'function') {
                targetElement.removeEventListener('keydown', handleKeyDown, false)
            }
        }
    }, [handleKeyDown, disabled, target])

    // Return useful information for debugging/testing
    return {
        normalizedKeys,
        disabled,
    }
}

export default useSimpleKeyboardShortcut
